import {
  addHours,
  addMinutes,
  differenceInMilliseconds,
  startOfHour,
  startOfMinute,
} from "date-fns";
import { useEffect, useState } from "react";
import { serverNow } from "@/utils/serverClock";

type Boundary = "minute" | "hour";

/** ms until the next clean minute/hour mark — keeps ticks aligned instead of drifting. */
const delayToNextBoundary = (from: Date, boundary: Boundary) => {
  const next =
    boundary === "hour" ? startOfHour(addHours(from, 1)) : startOfMinute(addMinutes(from, 1));
  return Math.max(differenceInMilliseconds(next, from), 250);
};

/**
 * A `Date` that refreshes on each clean boundary rather than on a fixed interval,
 * so it self-corrects for drift, tab throttling and DST shifts. One self-rescheduling
 * timeout, no `setInterval` to leak.
 */
export const useNow = (boundary: Boundary = "minute"): Date => {
  const [now, setNow] = useState<Date>(() => new Date(serverNow()));

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      const current = new Date(serverNow());
      setNow(current);
      timeoutId = setTimeout(tick, delayToNextBoundary(current, boundary));
    };

    timeoutId = setTimeout(tick, delayToNextBoundary(new Date(serverNow()), boundary));
    return () => clearTimeout(timeoutId);
  }, [boundary]);

  return now;
};
